import { useState, useRef } from 'react';
import { useLanguage } from '@/i18n/LanguageContext';
import { motion } from 'framer-motion';
import { Award, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useCertificates, getTranslation } from '@/hooks/useSupabaseData';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';

const CertificatesHighlights = () => {
  const { t, lang } = useLanguage();
  const { data: certs, isLoading } = useCertificates();
  const [lightboxImg, setLightboxImg] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isRtl = lang === 'he' || lang === 'ar';

  const scroll = (direction: 'prev' | 'next') => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const cardWidth = container.querySelector('.snap-center')?.getBoundingClientRect().width || 220;
    const scrollAmount = cardWidth + 24; // card width + gap
    const sign = direction === 'next' ? 1 : -1;
    // In RTL, positive scrollLeft goes left (visually forward), so we flip
    const rtlFlip = isRtl ? -1 : 1;
    container.scrollBy({
      left: sign * rtlFlip * scrollAmount,
      behavior: 'smooth',
    });
  };

  // In RTL: "prev" arrow is on the right (end), "next" is on the left (start)
  // Visual chevron direction: prev = ChevronRight in RTL, ChevronLeft in LTR
  const PrevIcon = isRtl ? ChevronRight : ChevronLeft;
  const NextIcon = isRtl ? ChevronLeft : ChevronRight;

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gradient-gold mb-4">{t('section.certificates_title')}</h2>
        </motion.div>

        <div className="relative group/section">
          {/* Prev arrow */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute start-0 top-1/2 -translate-y-1/2 z-10 bg-background/80 backdrop-blur border border-border/50 shadow-lg md:opacity-0 md:group-hover/section:opacity-100 transition-opacity rounded-full"
            onClick={() => scroll('prev')}
            aria-label="Previous"
          >
            <PrevIcon className="w-5 h-5" />
          </Button>

          {/* Next arrow */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute end-0 top-1/2 -translate-y-1/2 z-10 bg-background/80 backdrop-blur border border-border/50 shadow-lg md:opacity-0 md:group-hover/section:opacity-100 transition-opacity rounded-full"
            onClick={() => scroll('next')}
            aria-label="Next"
          >
            <NextIcon className="w-5 h-5" />
          </Button>

          <div
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto scrollbar-hide px-8 snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-56 w-48 shrink-0 rounded-xl" />)
            ) : certs?.length ? (
              certs.map((cert, i) => {
                const tr = getTranslation(cert.certificate_translations, lang);
                return (
                  <motion.div
                    key={cert.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                    className="snap-center shrink-0"
                  >
                    <Card
                      className="bg-card/50 border-border/50 hover:border-primary/30 transition-all text-center group cursor-pointer w-52"
                      onClick={() => cert.image_url && setLightboxImg(cert.image_url)}
                    >
                      <CardContent className="p-4 flex flex-col items-center">
                        <div className="w-40 h-40 rounded-lg bg-primary/5 flex items-center justify-center mb-3 overflow-hidden">
                          {cert.image_url ? (
                            <img src={cert.image_url} alt={tr?.issuer || tr?.title || ''} className="w-full h-full object-contain" />
                          ) : (
                            <Award className="w-10 h-10 text-primary" />
                          )}
                        </div>
                        <p className="text-sm font-medium line-clamp-2">{tr?.issuer || ''}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })
            ) : null}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightboxImg(null)}
        >
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('common.close')}
            className="absolute top-4 end-4 text-white hover:bg-white/20 rounded-full"
            onClick={() => setLightboxImg(null)}
          >
            <X className="w-6 h-6" />
          </Button>
          <img
            src={lightboxImg}
            alt=""
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
};

export default CertificatesHighlights;
